import { createContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import analyticsService from "./AnalyticsService";
import {
  AnalyticsErrorBoundary,
  setupGlobalErrorTracking,
} from "./utils/errorTracker";
import { usePageTracker } from "./utils/pageTracker";
import { usePerformanceTracker } from "./utils/performanceTracker";
import { useSessionTracker } from "./utils/sessionTracker";
import { useInteractionTracker } from "./utils/interactionTracker";
import { CUSTOM_DIMENSIONS, resolveEnvironment } from "./constants";

const GAContext = createContext(null);

const GAProvider = ({ children }) => {
  const authReducer = useSelector((state) => state?.authReducer);
  const orgReducer = useSelector((state) => state?.orgReducer);
  const currentDashboard = useSelector(
    (state) => state?.dashboardReducer?.currentDashboard,
  );
  const selectedCard = useSelector(
    (state) => state?.dashboardReducer?.selectedCard,
  );
  const environment = resolveEnvironment();
  const userData = authReducer?.userData || {};
  const userId = authReducer?.userData?.client_User_Id || null;
  const firstName = userData?.firstName?.trim();
  const lastName = userData?.lastName?.trim();
  const email = userData?.userName?.trim();
  const userName = [firstName, lastName].filter(Boolean).join(" ") || null;
  const userRole = userData?.role?.trim();
  const userSubscription = userData?.subscriptionName?.trim();

  const clientId = authReducer?.userData?.client_Id;
  const clientName = authReducer?.userData?.name;

  const analyticsContext = useMemo(() => {
    const selectedFI = orgReducer?.selectedFI || {};
    return {
      [CUSTOM_DIMENSIONS.ENVIRONMENT]: environment || null,
      [CUSTOM_DIMENSIONS.USER_ID]: userId || null,
      [CUSTOM_DIMENSIONS.USER_NAME]: userName || null,
      [CUSTOM_DIMENSIONS.FIRST_NAME]: firstName || null,
      [CUSTOM_DIMENSIONS.LAST_NAME]: lastName || null,
      [CUSTOM_DIMENSIONS.EMAIL]: email || null,
      [CUSTOM_DIMENSIONS.USER_ROLE]: userRole || null,
      [CUSTOM_DIMENSIONS.SUBSCRIPTION_TYPE]: userSubscription || null,
      [CUSTOM_DIMENSIONS.CLIENT_ID]: clientId || null,
      [CUSTOM_DIMENSIONS.CLIENT_NAME]: clientName || null,
      [CUSTOM_DIMENSIONS.FINANCIAL_INSTITUTION_ID]:
        selectedFI?.orgClientId || null,
      [CUSTOM_DIMENSIONS.FINANCIAL_INSTITUTION]: selectedFI?.org_name || null,
      [CUSTOM_DIMENSIONS.IDRSSD]: selectedFI?.idrssd || null,
      [CUSTOM_DIMENSIONS.INSTITUTION_TYPE]: selectedFI?.fi_Type || null,
      [CUSTOM_DIMENSIONS.DASHBOARD]:
        currentDashboard?.CONFIG?.pageHeader?.pageTitle || null,
      [CUSTOM_DIMENSIONS.NAVIGATED_FROM_CARD]:
        currentDashboard?.KEY === 2
          ? selectedCard?.commercial_title || null
          : selectedCard?.retail_title || null,
    };
  }, [environment, userData, orgReducer, currentDashboard, selectedCard]);

  useEffect(() => {
    // analyticsService.init({ disableOnLocal: false });
    analyticsService.init();
  }, []);

  useEffect(() => {
    analyticsService.setContext(analyticsContext);
  }, [analyticsContext]);

  useEffect(() => {
    if (!authReducer?.isLogin) {
      analyticsService.clearUser();
      return;
    }

    const profile = {
      [CUSTOM_DIMENSIONS.ENVIRONMENT]: environment || null,
      [CUSTOM_DIMENSIONS.USER_ID]: analyticsContext?.user_id || null,
      [CUSTOM_DIMENSIONS.USER_NAME]: analyticsContext?.user_name || null,
      [CUSTOM_DIMENSIONS.FIRST_NAME]: analyticsContext?.first_name || null,
      [CUSTOM_DIMENSIONS.LAST_NAME]: analyticsContext?.last_name || null,
      [CUSTOM_DIMENSIONS.EMAIL]: analyticsContext?.email || null,
      [CUSTOM_DIMENSIONS.USER_ROLE]: analyticsContext?.user_role || null,
      [CUSTOM_DIMENSIONS.SUBSCRIPTION_TYPE]:
        analyticsContext.subscription_type || null,
      [CUSTOM_DIMENSIONS.CLIENT_ID]: analyticsContext?.client_id || null,
      [CUSTOM_DIMENSIONS.CLIENT_NAME]: analyticsContext?.client_name || null,
      [CUSTOM_DIMENSIONS.FINANCIAL_INSTITUTION_ID]:
        analyticsContext?.financial_institution_id || null,
      [CUSTOM_DIMENSIONS.FINANCIAL_INSTITUTION]:
        analyticsContext?.financial_institution || null,
      [CUSTOM_DIMENSIONS.IDRSSD]: analyticsContext?.idrssd || null,
    };

    analyticsService.setUser(userId, profile);
    analyticsService.setLoginTime(
      authReducer?.loggedInTime || new Date().toISOString(),
    );
  }, [authReducer, orgReducer, analyticsContext.subscription_type]);

  useEffect(() => {
    return setupGlobalErrorTracking();
  }, []);

  usePageTracker();
  usePerformanceTracker();
  useSessionTracker();
  useInteractionTracker();

  return (
    <GAContext.Provider value={analyticsContext}>
      <AnalyticsErrorBoundary>{children}</AnalyticsErrorBoundary>
    </GAContext.Provider>
  );
};

GAProvider.propTypes = {
  children: PropTypes.node,
};

export default GAProvider;
export { GAContext };
